import { useQuery, UseQueryResult } from 'react-query';
import { gql } from '@apollo/client';
import { graphqlClient } from 'bk-web-app';
import { ErrorWithData } from 'task-manager/bench-services-api/ErrorWithData';
import { BusinessService } from '../types';

const CLIENT_SERVICE_BY_ADD_ON_QUERY = gql`
  query ClientServiceByAddOn($addOnId: ID!) {
    clientServiceByAddOn(addOnId: $addOnId) {
      id
      client {
        id
      }
      globalService {
        id
      }
      addOn {
        id
      }
      uniqueIdentifier
      effectiveStartDate
      effectiveEndDate
      entityVersion
    }
  }
`;

interface ClientServiceResponse {
  clientServiceByAddOn: {
    id: string;
    client: { id: string };
    globalService: { id: string };
    addOn?: { id: string } | null;
    uniqueIdentifier?: string | null;
    effectiveStartDate?: string;
    effectiveEndDate?: string;
    entityVersion?: string;
  } | null;
}

function mapClientServiceToBusinessService(
  clientService: NonNullable<ClientServiceResponse['clientServiceByAddOn']>,
): BusinessService {
  return {
    businessServiceId: clientService.id,
    businessId: Number(clientService.client.id),
    globalServiceId: clientService.globalService.id,
    uniqueIdentifier: clientService.uniqueIdentifier,
    effectiveStartDate: clientService.effectiveStartDate,
    effectiveEndDate: clientService.effectiveEndDate,
    addOnId: clientService.addOn?.id ?? '',
    createdAt: '',
    createdBy: '',
  };
}

async function getViaGraphQL(addOnId: string): Promise<BusinessService | undefined> {
  const { data } = await graphqlClient.query<ClientServiceResponse>({
    query: CLIENT_SERVICE_BY_ADD_ON_QUERY,
    variables: { addOnId },
    context: { useV2: true },
    fetchPolicy: 'network-only',
  });

  if (!data.clientServiceByAddOn) return undefined;
  return mapClientServiceToBusinessService(data.clientServiceByAddOn);
}

async function get(apiUrl: string, addOnId: string): Promise<BusinessService | undefined> {
  if (!apiUrl) throw new Error('Required properties are not set');
  if (!addOnId) return;

  const response: Response = await fetch(`${apiUrl}/add-on/${addOnId}/business-service`, {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = (await response.json()) as Record<string, unknown>;
    throw new ErrorWithData('Failed to get business service', error);
  }
  return (await response.json()) as BusinessService | undefined;
}

interface UseFetchBusinessServiceProps {
  addOnId?: string;
  useGraphQL?: boolean;
}

export const useFetchBusinessService = ({
  addOnId,
  useGraphQL = false,
}: UseFetchBusinessServiceProps): UseQueryResult<BusinessService | undefined> => {
  const apiUrl = window.BENCH?.auApi?.SETUP_BUSINESS_SERVICE_API_URL || '';

  return useQuery({
    queryKey: ['getBusinessService', { addOnId, useGraphQL }],
    queryFn: () => {
      if (!addOnId) {
        return Promise.resolve(undefined);
      }
      if (useGraphQL) {
        return getViaGraphQL(addOnId);
      }
      return get(apiUrl, addOnId);
    },
    retry: false,
  });
};
